import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./AsbMessagesPage.css";
import useApiBase from "./api/useApiBase";
import AuthGate from "./auth/AuthGate";
import useAuthSession from "./auth/useAuthSession";
import usePageTitle from "./usePageTitle";

const clampLimit = (value) => {
  if (!Number.isFinite(value)) {
    return 10;
  }
  if (value < 1) {
    return 1;
  }
  if (value > 200) {
    return 200;
  }
  return value;
};

const formatCellValue = (value) => {
  if (value === null || value === undefined) {
    return "N/A";
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  }
  return String(value);
};

function DbTablesPage() {
  usePageTitle("DB Tables");
  const [tables, setTables] = useState([]);
  const [openTables, setOpenTables] = useState(new Set());
  const [tableState, setTableState] = useState({});
  const [error, setError] = useState("");
  const [loadingTables, setLoadingTables] = useState(true);
  const [limitInput, setLimitInput] = useState("10");
  const lastRequestRef = useRef({});
  const apiBase = useApiBase();
  const auth = useAuthSession(apiBase);

  const limitValue = useMemo(() => {
    const parsed = Number(limitInput);
    return clampLimit(Number.isFinite(parsed) ? parsed : 10);
  }, [limitInput]);

  const fetchTables = useCallback(async () => {
    setLoadingTables(true);
    try {
      const response = await auth.fetchWithAuth("/db-tables");
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }
      const payload = await response.json();
      const nextTables = Array.isArray(payload?.tables) ? payload.tables : [];
      setTables(nextTables);
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoadingTables(false);
    }
  }, [auth.fetchWithAuth]);

  const fetchRows = useCallback(async (tableName, filters, force = false) => {
    if (!tableName) {
      return;
    }
    const {
      limit,
      lineNo,
      minDate,
      maxDate,
      lineColumn,
      dateColumn,
    } = filters || {};
    const resolvedLimit = Number.isFinite(limit) ? limit : 10;
    const requestKey = JSON.stringify({
      limit: resolvedLimit,
      lineNo,
      minDate,
      maxDate,
      lineColumn,
      dateColumn,
    });
    if (!force) {
      const last = lastRequestRef.current[tableName];
      if (last === requestKey) {
        return;
      }
    }
    lastRequestRef.current[tableName] = requestKey;
    setTableState((prev) => ({
      ...prev,
      [tableName]: {
        ...(prev[tableName] ?? {}),
        loading: true,
        error: "",
      },
    }));
    try {
      const response = await auth.fetchWithAuth(
        `/db-tables/${encodeURIComponent(tableName)}`,
        {
          params: {
            limit: resolvedLimit,
            line_no: lineNo || undefined,
            min_date: minDate || undefined,
            max_date: maxDate || undefined,
            line_column: lineColumn || undefined,
            date_column: dateColumn || undefined,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }
      const payload = await response.json();
      const nextRows = Array.isArray(payload?.rows) ? payload.rows : [];
      const nextColumns = Array.isArray(payload?.columns)
        ? payload.columns
        : nextRows.length > 0
        ? Object.keys(nextRows[0])
        : [];
      const nextLineColumns = Array.isArray(payload?.line_columns)
        ? payload.line_columns
        : [];
      const nextDateColumns = Array.isArray(payload?.date_columns)
        ? payload.date_columns
        : [];
      setTableState((prev) => {
        const prevEntry = prev[tableName] ?? {};
        const nextColumns = Array.isArray(payload?.columns)
          ? payload.columns
          : nextRows.length > 0
          ? Object.keys(nextRows[0])
          : [];
        const nextLineColumns = Array.isArray(payload?.line_columns)
          ? payload.line_columns
          : [];
        const nextDateColumns = Array.isArray(payload?.date_columns)
          ? payload.date_columns
          : [];
        const prevSelected = Array.isArray(prevEntry.selectedColumns)
          ? prevEntry.selectedColumns
          : [];
        const selectedColumns = prevSelected.length > 0
          ? prevSelected.filter((col) => nextColumns.includes(col))
          : nextColumns;
        const nextLineColumn = nextLineColumns.includes(prevEntry.lineColumn)
          ? prevEntry.lineColumn
          : nextLineColumns[0] || "";
        const nextDateColumn = nextDateColumns.includes(prevEntry.dateColumn)
          ? prevEntry.dateColumn
          : nextDateColumns[0] || "";
        return {
        ...prev,
        [tableName]: {
          rows: nextRows,
          columns: nextColumns,
          lineColumns: nextLineColumns,
          dateColumns: nextDateColumns,
          selectedColumns,
          limitInput: prevEntry.limitInput ?? String(resolvedLimit),
          lineNoInput: prevEntry.lineNoInput ?? "",
          minDateInput: prevEntry.minDateInput ?? "",
          maxDateInput: prevEntry.maxDateInput ?? "",
          lineColumn: nextLineColumn,
          dateColumn: nextDateColumn,
          loading: false,
          error: "",
          lastFetchedAt: new Date(),
        },
        };
      });
    } catch (err) {
      setTableState((prev) => {
        const prevEntry = prev[tableName] ?? {};
        return {
          ...prev,
          [tableName]: {
            ...prevEntry,
            rows: [],
            columns: prevEntry.columns ?? [],
            lineColumns: prevEntry.lineColumns ?? [],
            dateColumns: prevEntry.dateColumns ?? [],
            loading: false,
            error: err instanceof Error ? err.message : "Unknown error",
            lastFetchedAt: prevEntry.lastFetchedAt ?? null,
          },
        };
      });
    } finally {
      setTableState((prev) => ({
        ...prev,
        [tableName]: {
          ...(prev[tableName] ?? {}),
          loading: false,
        },
      }));
    }
  }, [auth.fetchWithAuth]);

  useEffect(() => {
    if (!auth.authChecked || !auth.isAuthenticated) {
      return;
    }
    fetchTables();
  }, [auth.authChecked, auth.isAuthenticated, fetchTables]);

  const totalOpen = useMemo(() => openTables.size, [openTables]);
  const totalRows = useMemo(() => (
    Object.values(tableState).reduce((sum, entry) => sum + (entry?.rows?.length ?? 0), 0)
  ), [tableState]);

  const getTableLimit = useCallback((tableName) => {
    const entry = tableState[tableName];
    const raw = entry?.limitInput ?? limitInput;
    const parsed = Number(raw);
    return clampLimit(Number.isFinite(parsed) ? parsed : 10);
  }, [limitInput, tableState]);

  const getTableFilters = useCallback((tableName) => {
    const entry = tableState[tableName] ?? {};
    const lineNoRaw = entry.lineNoInput ?? "";
    const lineNo = lineNoRaw === "" ? undefined : Number(lineNoRaw);
    const parsedLineNo = Number.isFinite(lineNo) ? lineNo : undefined;
    return {
      limit: getTableLimit(tableName),
      lineNo: parsedLineNo,
      minDate: entry.minDateInput || "",
      maxDate: entry.maxDateInput || "",
      lineColumn: entry.lineColumn || "",
      dateColumn: entry.dateColumn || "",
    };
  }, [getTableLimit, tableState]);

  const toggleTable = useCallback((tableName) => {
    const isOpen = openTables.has(tableName);
    setOpenTables((prev) => {
      const next = new Set(prev);
      if (isOpen) {
        next.delete(tableName);
      } else {
        next.add(tableName);
      }
      return next;
    });
    if (!isOpen) {
      fetchRows(tableName, getTableFilters(tableName));
    }
  }, [fetchRows, getTableFilters, openTables]);

  return (
    <AuthGate auth={auth} title="DB Tables Login">
      <div className="asb-page">
        <div className="asb-container">
          <header className="asb-hero">
            <div>
              <p className="asb-eyebrow">Database browser</p>
              <h1 className="asb-title">DB tables</h1>
              <p className="asb-subtitle">
                Showing latest records for model-backed tables
              </p>
            </div>
            <div className="asb-metrics">
              <div>
                <span>Tables</span>
                <strong>{tables.length}</strong>
              </div>
              <div>
                <span>Open</span>
                <strong>{totalOpen}</strong>
              </div>
              <div>
                <span>Rows</span>
                <strong>{totalRows}</strong>
              </div>
            </div>
          </header>

          <nav className="asb-kinds-nav">
            {tables.map((table) => (
              <a
                key={table}
                href={`#table-${table}`}
                className="asb-kinds-nav-item db-kinds-nav-item"
              >
                {table}
              </a>
            ))}
          </nav>

          <div className="asb-filter">
            <div>
              <label htmlFor="db-table-limit">Default limit</label>
              <input
                id="db-table-limit"
                type="number"
                inputMode="numeric"
                min="1"
                max="200"
                value={limitInput}
                onChange={(event) => setLimitInput(event.target.value)}
              />
            </div>
            <button
              type="button"
              onClick={() => {
                openTables.forEach((table) => {
                  fetchRows(table, getTableFilters(table), true);
                });
              }}
            >
              Refresh open tables
            </button>
          </div>

          {error && <div className="asb-error">Error: {error}</div>}

          {loadingTables ? (
            <div className="asb-loader">
              <div className="asb-spinner" />
              <p>Loading tables...</p>
            </div>
          ) : tables.length === 0 ? (
            <div className="asb-empty">No DB tables are available.</div>
          ) : (
            <div className="asb-kinds">
              {tables.map((table) => {
                const entry = tableState[table] ?? {};
                const isOpen = openTables.has(table);
                const rows = Array.isArray(entry.rows) ? entry.rows : [];
                const columns = Array.isArray(entry.columns) ? entry.columns : [];
                const selectedColumns = Array.isArray(entry.selectedColumns)
                  ? entry.selectedColumns
                  : columns;
                const visibleColumns = selectedColumns;
                const lineColumns = Array.isArray(entry.lineColumns)
                  ? entry.lineColumns
                  : [];
                const dateColumns = Array.isArray(entry.dateColumns)
                  ? entry.dateColumns
                  : [];
                const lineColumn = entry.lineColumn || "";
                const dateColumn = entry.dateColumn || "";
                const lineNoInput = entry.lineNoInput ?? "";
                const minDateInput = entry.minDateInput ?? "";
                const maxDateInput = entry.maxDateInput ?? "";
                const sectionLimitRaw = entry.limitInput ?? limitInput;
                const sectionLimit = getTableLimit(table);
                const tableError = entry.error;
                const lastFetchedAt = entry.lastFetchedAt;

                return (
                  <section className="asb-section" key={table} id={`table-${table}`}>
                    <header className="asb-section-header">
                      <div>
                        <span className="asb-badge asb-badge-other">{table}</span>
                        {isOpen && <h2>Latest records</h2>}
                      </div>
                      <div className="asb-section-stats">
                        <span>{isOpen ? `Rows ${rows.length}` : "Collapsed"}</span>
                        <span>Limit {sectionLimit}</span>
                      </div>
                    </header>
                    <div className="asb-section-controls">
                      <label htmlFor={`db-table-limit-${table}`}>
                        Rows
                      </label>
                      <input
                        id={`db-table-limit-${table}`}
                        type="number"
                        inputMode="numeric"
                        min="1"
                        max="200"
                        value={sectionLimitRaw}
                        onChange={(event) =>
                          setTableState((prev) => ({
                            ...prev,
                            [table]: {
                              ...(prev[table] ?? {}),
                              limitInput: event.target.value,
                            },
                          }))
                        }
                      />
                      <label htmlFor={`db-table-line-${table}`}>
                        Line
                      </label>
                      <input
                        id={`db-table-line-${table}`}
                        type="number"
                        inputMode="numeric"
                        value={lineNoInput}
                        onChange={(event) =>
                          setTableState((prev) => ({
                            ...prev,
                            [table]: {
                              ...(prev[table] ?? {}),
                              lineNoInput: event.target.value,
                            },
                          }))
                        }
                        placeholder={lineColumns.length > 0 ? "" : "N/A"}
                        disabled={lineColumns.length === 0}
                      />
                      {lineColumns.length > 1 ? (
                        <select
                          value={lineColumn}
                          onChange={(event) =>
                            setTableState((prev) => ({
                              ...prev,
                              [table]: {
                                ...(prev[table] ?? {}),
                                lineColumn: event.target.value,
                              },
                            }))
                          }
                        >
                          {lineColumns.map((col) => (
                            <option key={col} value={col}>
                              {col}
                            </option>
                          ))}
                        </select>
                      ) : null}
                      <label htmlFor={`db-table-min-date-${table}`}>
                        Min datetime
                      </label>
                      <input
                        id={`db-table-min-date-${table}`}
                        type="datetime-local"
                        step="1"
                        value={minDateInput}
                        onChange={(event) =>
                          setTableState((prev) => ({
                            ...prev,
                            [table]: {
                              ...(prev[table] ?? {}),
                              minDateInput: event.target.value,
                            },
                          }))
                        }
                        disabled={dateColumns.length === 0}
                      />
                      <label htmlFor={`db-table-max-date-${table}`}>
                        Max datetime
                      </label>
                      <input
                        id={`db-table-max-date-${table}`}
                        type="datetime-local"
                        step="1"
                        value={maxDateInput}
                        onChange={(event) =>
                          setTableState((prev) => ({
                            ...prev,
                            [table]: {
                              ...(prev[table] ?? {}),
                              maxDateInput: event.target.value,
                            },
                          }))
                        }
                        disabled={dateColumns.length === 0}
                      />
                      {dateColumns.length > 1 ? (
                        <select
                          value={dateColumn}
                          onChange={(event) =>
                            setTableState((prev) => ({
                              ...prev,
                              [table]: {
                                ...(prev[table] ?? {}),
                                dateColumn: event.target.value,
                              },
                            }))
                          }
                        >
                          {dateColumns.map((col) => (
                            <option key={col} value={col}>
                              {col}
                            </option>
                          ))}
                        </select>
                      ) : null}
                      <div className="db-columns-control">
                        {columns.length === 0 ? (
                          <span className="db-columns-empty">Load rows to see columns.</span>
                        ) : (
                          <details className="db-columns-dropdown">
                            <summary>
                              Columns ({selectedColumns.length}/{columns.length})
                            </summary>
                            <div className="db-columns-panel">
                              <div className="db-columns-actions">
                                <button
                                  type="button"
                                  className="asb-section-toggle"
                                  onClick={() =>
                                    setTableState((prev) => ({
                                      ...prev,
                                      [table]: {
                                        ...(prev[table] ?? {}),
                                        selectedColumns: columns,
                                      },
                                    }))
                                  }
                                >
                                  All
                                </button>
                                <button
                                  type="button"
                                  className="asb-section-toggle"
                                  onClick={() =>
                                    setTableState((prev) => ({
                                      ...prev,
                                      [table]: {
                                        ...(prev[table] ?? {}),
                                        selectedColumns: [],
                                      },
                                    }))
                                  }
                                >
                                  None
                                </button>
                              </div>
                              <div className="db-columns-grid">
                                {columns.map((col) => (
                                  <label key={col} className="db-columns-item">
                                    <input
                                      type="checkbox"
                                      checked={selectedColumns.includes(col)}
                                      onChange={(event) => {
                                        const checked = event.target.checked;
                                        setTableState((prev) => {
                                          const prevEntry = prev[table] ?? {};
                                          const prevSelected = Array.isArray(prevEntry.selectedColumns)
                                            ? prevEntry.selectedColumns
                                            : [];
                                          const nextSelected = checked
                                            ? [...new Set([...prevSelected, col])]
                                            : prevSelected.filter((item) => item !== col);
                                          return {
                                            ...prev,
                                            [table]: {
                                              ...prevEntry,
                                              selectedColumns: nextSelected,
                                            },
                                          };
                                        });
                                      }}
                                    />
                                    <span>{col}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          </details>
                        )}
                      </div>
                      <button
                        type="button"
                        className={`asb-section-toggle ${isOpen ? "asb-section-toggle-open" : ""}`}
                        onClick={() => toggleTable(table)}
                        aria-label={isOpen ? `Hide ${table}` : `Show ${table}`}
                      >
                        {isOpen ? "Hide" : "Open"}
                      </button>
                      {isOpen && (
                        <button
                          type="button"
                          className="asb-section-toggle"
                          onClick={() => fetchRows(table, getTableFilters(table), true)}
                        >
                          Refresh
                        </button>
                      )}
                      {isOpen && lastFetchedAt && (
                        <span>Updated {lastFetchedAt.toLocaleTimeString()}</span>
                      )}
                    </div>
                    {isOpen && entry.loading ? (
                      <div className="asb-loader">
                        <div className="asb-spinner" />
                        <p>Loading rows...</p>
                      </div>
                    ) : null}
                    {isOpen && tableError ? (
                      <div className="asb-error">Error: {tableError}</div>
                    ) : null}
                    {isOpen && !entry.loading && !tableError && rows.length === 0 ? (
                      <div className="asb-empty">No rows available for this table.</div>
                    ) : null}
                    {isOpen && !entry.loading && !tableError && rows.length > 0 ? (
                      <div className="asb-packages db-table-scroll">
                        <table className="asb-packages-table">
                          <thead>
                            <tr>
                              {visibleColumns.map((col) => (
                                <th key={col}>{col}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row, rowIndex) => (
                              <tr key={`${table}-${rowIndex}`}>
                                {visibleColumns.map((col) => (
                                  <td key={`${table}-${rowIndex}-${col}`}>
                                    {formatCellValue(row[col])}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : null}
                    {isOpen && !entry.loading && !tableError && rows.length > 0 && visibleColumns.length === 0 ? (
                      <div className="asb-empty">Select at least one column.</div>
                    ) : null}
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AuthGate>
  );
}

export default DbTablesPage;
